import { ImproveTemplateDto } from "../../../Types/Improve/ImproveTemplateDto";
import {
  IMPROVE_EXPORT_COLS,
  IMPROVE_PLAN_COLS,
  ImproveExportColumnKey,
  ImproveExportFieldVisibility,
} from "./types";

const COLUMN_TO_TEMPLATE_FIELD: Partial<
  Record<ImproveExportColumnKey, keyof ImproveTemplateDto>
> = {
  Objective: "objective",
  "Improvement status": "status",
  "Assigned to": "assignedTo",
  "Target date": "targetDate",
  "Completion date": "completionDate",
  "Expected impact": "expectedImpact",
  "Actual impact": "actualImpact",
  Tags: "tags",
  "Custom tags": "customTagA",
  "Custom date": "customDateA",
  "Column A": "columnA",
  "Column B": "columnB",
  "Column C": "columnC",
  "URL 1": "url1",
  "URL 2": "url2",
  "URL 3": "url3",
  "Task heading": "taskHeading",
  "Task status": "taskStatus",
  "Task target date": "taskTargetDate",
  "Task completion date": "taskCompletionDate",
  "Task assigned to": "taskAssignedTo",
  "Column D": "columnD",
  "Column E": "columnE",
  "Column F": "columnF",
  "Task custom tags": "customTagB",
};

/** Fallback labels when template vocabulary is unavailable (matches plan UI). */
export const IMPROVE_EXPORT_COLUMN_DEFAULT_LABELS: Record<
  ImproveExportColumnKey,
  string
> = {
  "Plan title": "Plan title",
  "Due date": "Due date",
  Owner: "Owner",
  "Plan status": "Plan status",
  Objective: "Objective",
  "Improvement status": "Status",
  "Assigned to": "Assigned To",
  "Target date": "Target Date",
  "Completion date": "Completion Date",
  "Expected impact": "Expected Impact",
  "Actual impact": "Actual Impact",
  Tags: "Tags",
  "Custom tags": "Custom Tags",
  "Custom date": "Custom Date A",
  "Column A": "Column A",
  "Column B": "Column B",
  "Column C": "Column C",
  "URL 1": "URL 1",
  "URL 2": "URL 2",
  "URL 3": "URL 3",
  "Task heading": "Task heading",
  "Task status": "Status",
  "Task target date": "Target Date",
  "Task completion date": "Completion Date",
  "Task assigned to": "Assigned To",
  "Column D": "Column D",
  "Column E": "Column E",
  "Column F": "Column F",
  "Task custom tags": "Tag Column B",
};

function templateFieldName(
  template: ImproveTemplateDto,
  fieldKey: keyof ImproveTemplateDto
): string | undefined {
  const config = template[fieldKey];
  if (!config || typeof config !== "object" || !("name" in config)) {
    return undefined;
  }
  const name = config.name?.trim();
  return name || undefined;
}

export function getImproveExportColumnLabel(
  column: ImproveExportColumnKey,
  templates: ImproveTemplateDto[]
): string {
  const defaultLabel = IMPROVE_EXPORT_COLUMN_DEFAULT_LABELS[column];
  const fieldKey = COLUMN_TO_TEMPLATE_FIELD[column];
  if (!fieldKey || templates.length === 0) {
    return defaultLabel;
  }

  const names = templates
    .map((t) => templateFieldName(t, fieldKey))
    .filter((n): n is string => Boolean(n));

  if (names.length === 0) {
    return defaultLabel;
  }

  const unique = Array.from(new Set(names));
  return unique.length === 1 ? unique[0] : defaultLabel;
}

export function getImproveExportColumnLabelMap(
  templates: ImproveTemplateDto[]
): Record<ImproveExportColumnKey, string> {
  return IMPROVE_EXPORT_COLS.reduce(
    (acc, k) => ({
      ...acc,
      [k]: getImproveExportColumnLabel(k, templates),
    }),
    {} as Record<ImproveExportColumnKey, string>
  );
}

export function splitImproveExportColumnsByToggleable(
  toggleableMap: Record<ImproveExportColumnKey, boolean>
): {
  enabled: ImproveExportColumnKey[];
  disabled: ImproveExportColumnKey[];
} {
  const enabled: ImproveExportColumnKey[] = [];
  const disabled: ImproveExportColumnKey[] = [];

  for (const key of IMPROVE_EXPORT_COLS) {
    if (toggleableMap[key]) {
      enabled.push(key);
    } else {
      disabled.push(key);
    }
  }

  return { enabled, disabled };
}

export const IMPROVE_EXPORT_DISABLED_FIELD_TOOLTIP =
  "This field is not enabled in this template";

export function isImproveExportColumnEnabledInTemplate(
  column: ImproveExportColumnKey,
  template: ImproveTemplateDto
): boolean {
  if ((IMPROVE_PLAN_COLS as readonly string[]).includes(column)) {
    return true;
  }

  const fieldKey = COLUMN_TO_TEMPLATE_FIELD[column];
  if (!fieldKey) {
    return true;
  }

  const config = template[fieldKey];
  if (!config || typeof config !== "object" || !("shown" in config)) {
    return false;
  }

  return config.shown;
}

export function isImproveExportColumnToggleable(
  column: ImproveExportColumnKey,
  templates: ImproveTemplateDto[]
): boolean {
  if (templates.length === 0) {
    return true;
  }

  return templates.some((t) => isImproveExportColumnEnabledInTemplate(column, t));
}

export function getImproveExportColumnToggleableMap(
  templates: ImproveTemplateDto[]
): Record<ImproveExportColumnKey, boolean> {
  return IMPROVE_EXPORT_COLS.reduce(
    (acc, k) => ({
      ...acc,
      [k]: isImproveExportColumnToggleable(k, templates),
    }),
    {} as Record<ImproveExportColumnKey, boolean>
  );
}

export function buildInitialImproveExportFieldVisibility(
  templates: ImproveTemplateDto[]
): ImproveExportFieldVisibility {
  const toggleable = getImproveExportColumnToggleableMap(templates);
  return IMPROVE_EXPORT_COLS.reduce(
    (acc, k) => ({ ...acc, [k]: toggleable[k] }),
    {} as ImproveExportFieldVisibility
  );
}

export function clampImproveExportFieldVisibility(
  visibility: ImproveExportFieldVisibility,
  templates: ImproveTemplateDto[]
): ImproveExportFieldVisibility {
  const toggleable = getImproveExportColumnToggleableMap(templates);
  const next = { ...visibility };
  for (const k of IMPROVE_EXPORT_COLS) {
    if (!toggleable[k]) {
      next[k] = false;
    }
  }
  return next;
}
